import React, { useState } from 'react';
import { FieldArray, useFormikContext } from 'formik';
import {
  Bars3BottomLeftIcon,
  CalendarDaysIcon,
  DocumentTextIcon,
  EllipsisHorizontalIcon,
  EnvelopeIcon,
  LinkIcon,
  PencilSquareIcon,
  PlusIcon,
  ShoppingBagIcon,
  TicketIcon,
  TrashIcon,
  UserCircleIcon,
} from '@heroicons/react/24/outline';
import {
  LINK_IN_BIO_LIMITS,
  resolveLinkInBioItems,
  type LinkInBio,
  type LinkInBioBlock,
  type LinkInBioBlockType,
  type LinkInBioPageSummary,
  type LinkInBioPageType,
} from '@repo/lib';
import { GripVertical } from 'lucide-react';
import { getEnglishLinkInBioLabels } from '@repo/checkout';
import Reorderable from '@dashboard/components/Reorderable';
import BadgeIcon, { isBadgeIcon } from '@repo/checkout/components/BadgeIcon';
import { Button } from '@dashboard/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@dashboard/components/ui/dropdown-menu';
import { Switch } from '@dashboard/components/ui/switch';
import BlockEditor from './BlockEditor';
import SectionCard from './SectionCard';
import {
  BLOCK_TYPE_LABELS,
  blockHint,
  blockKindLabel,
  PAGE_TYPE_LABELS,
  blockTitle,
  canShowAsCard,
  scheduleNote,
} from './blockLabels';

const BLOCK_ICONS: Record<LinkInBioBlockType, React.ComponentType<{ className?: string }>> = {
  page: DocumentTextIcon,
  events: CalendarDaysIcon,
  link: LinkIcon,
  heading: Bars3BottomLeftIcon,
  portal: UserCircleIcon,
  email: EnvelopeIcon,
};

const PAGE_ICONS: Record<LinkInBioPageType, React.ComponentType<{ className?: string }>> = {
  checkout: ShoppingBagIcon,
  event: TicketIcon,
  form: DocumentTextIcon,
};

type AddOption = { key: string; type: LinkInBioBlockType; pageType?: LinkInBioPageType };

const ADD_OPTIONS: AddOption[] = [
  { key: 'checkout', type: 'page', pageType: 'checkout' },
  { key: 'event', type: 'page', pageType: 'event' },
  { key: 'form', type: 'page', pageType: 'form' },
  { key: 'events', type: 'events' },
  { key: 'link', type: 'link' },
  { key: 'email', type: 'email' },
  { key: 'heading', type: 'heading' },
  { key: 'portal', type: 'portal' },
];

const optionIcon = ({ type, pageType }: AddOption) =>
  pageType ? PAGE_ICONS[pageType] : BLOCK_ICONS[type];
const optionLabel = ({ type, pageType }: AddOption) =>
  pageType ? PAGE_TYPE_LABELS[pageType] : BLOCK_TYPE_LABELS[type];

const portalLabel = getEnglishLinkInBioLabels().portal;

const newBlock = (type: LinkInBioBlockType): LinkInBioBlock => ({
  type,
  title: null,
  url: null,
  page: null,
  limit: type === 'events' ? LINK_IN_BIO_LIMITS.eventsDefault : null,
  thumbnail: null,
  icon: null,
  style: type === 'events' ? 'card' : 'button',
  hidden: false,
});

type Editing = {
  block: LinkInBioBlock;
  index: number | null;
  pageType?: LinkInBioPageType | null;
} | null;

export default function BlocksSection({
  pages,
  upcomingEvents,
  isLoadingPages,
}: {
  pages: Record<string, LinkInBioPageSummary>;
  upcomingEvents: LinkInBioPageSummary[];
  isLoadingPages: boolean;
}) {
  const { values, setFieldValue } = useFormikContext<LinkInBio>();
  const [editing, setEditing] = useState<Editing>(null);
  const blocks = values.blocks ?? [];
  const now = new Date();
  // The page drops a heading with nothing shown under it; ask the resolver which ones survive.
  const shownHeadings = new Set(
    resolveLinkInBioItems({
      blocks,
      pages,
      upcomingEvents,
      now,
      paths: { pagePrefix: '', portal: '' },
    })
      .filter(item => item.kind === 'heading')
      .map(item => item.key),
  );
  const headingHint = (block: LinkInBioBlock, index: number) =>
    block.type === 'heading' && !isLoadingPages && !shownHeadings.has(block.id || `i${index}`)
      ? 'Nothing visible under it, not shown'
      : null;

  return (
    <FieldArray name="blocks">
      {({ push, remove, replace }) => (
        <SectionCard
          title="Blocks"
          description="What visitors see, in this order."
          actions={
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  size="sm"
                  disabled={!!editing || blocks.length >= LINK_IN_BIO_LIMITS.blocks}
                >
                  <PlusIcon />
                  Add block
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {ADD_OPTIONS.map(option => {
                  const Icon = optionIcon(option);
                  return (
                    <React.Fragment key={option.key}>
                      {option.key === 'events' && <DropdownMenuSeparator />}
                      <DropdownMenuItem
                        onSelect={() =>
                          option.type === 'portal'
                            ? push(newBlock('portal'))
                            : setEditing({
                                block: newBlock(option.type),
                                index: null,
                                pageType: option.pageType,
                              })
                        }
                      >
                        <Icon className="h-4 w-4" />
                        {optionLabel(option)}
                      </DropdownMenuItem>
                    </React.Fragment>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>
          }
        >
          {editing && (
            <BlockEditor
              block={editing.block}
              isNew={editing.index === null}
              pageType={editing.pageType}
              onClose={() => setEditing(null)}
              onSave={block => {
                if (editing.index === null) {
                  push(block);
                } else {
                  replace(editing.index, block);
                }
                setEditing(null);
              }}
            />
          )}

          {blocks.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Add your events, checkout pages, forms and links. Visitors see them in this order.
            </p>
          ) : (
            <Reorderable
              id="link-in-bio-blocks"
              className="divide-y rounded-lg border px-3"
              // Saving the editor replaces the block at the index it had when opened.
              isDragDisabled={!!editing}
              items={blocks.map((block, index) => ({ key: block.id ?? `new-${index}`, block }))}
              onReorder={(reordered: Array<{ block: LinkInBioBlock }>) =>
                setFieldValue(
                  'blocks',
                  reordered.map(item => item.block),
                )
              }
              renderItem={({
                item: { block },
                index,
                dragHandleProps,
              }: {
                item: { block: LinkInBioBlock };
                index: number;
                dragHandleProps: React.HTMLAttributes<HTMLDivElement>;
              }) => {
                const page = block.page ? pages[block.page] : undefined;
                const Icon =
                  block.type === 'page' && page ? PAGE_ICONS[page.type] : BLOCK_ICONS[block.type];
                const title = blockTitle(block, page, portalLabel);
                const hint =
                  blockHint(block, page, isLoadingPages, now) ?? headingHint(block, index);
                const note = scheduleNote(block, now);

                return (
                  <div className="flex items-center gap-3 py-2.5">
                    <div
                      {...dragHandleProps}
                      aria-label={`Drag to reorder ${title}`}
                      className="-ml-1 flex size-7 shrink-0 cursor-grab items-center justify-center rounded-md text-muted-foreground hover:bg-accent active:cursor-grabbing"
                    >
                      <GripVertical className="size-4" />
                    </div>
                    {block.icon && isBadgeIcon(block.icon) ? (
                      <BadgeIcon
                        icon={block.icon}
                        className="size-4 shrink-0 text-muted-foreground"
                      />
                    ) : (
                      <Icon className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium">{title}</p>
                      <p className="text-xs text-muted-foreground">
                        {blockKindLabel(block, page)}
                        {block.style === 'card' && canShowAsCard(block) && ' · Card'}
                        {note && ` · ${note}`}
                        {hint && (
                          <span className="text-amber-700 dark:text-amber-400"> · {hint}</span>
                        )}
                      </p>
                    </div>

                    <Switch
                      checked={!block.hidden}
                      aria-label={`Show ${title}`}
                      onCheckedChange={checked => setFieldValue(`blocks.${index}.hidden`, !checked)}
                    />

                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon-sm"
                          aria-label={`More actions for ${title}`}
                          disabled={!!editing}
                        >
                          <EllipsisHorizontalIcon />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          onSelect={() =>
                            setEditing({ block, index, pageType: page?.type ?? null })
                          }
                        >
                          <PencilSquareIcon className="size-4" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          className="text-destructive"
                          onSelect={() => remove(index)}
                        >
                          <TrashIcon className="size-4" />
                          Remove
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                );
              }}
            />
          )}
        </SectionCard>
      )}
    </FieldArray>
  );
}
